import React, { useState } from "react";
import { t } from "ttag";
import { Box, Button, Typography } from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { isEqualJson, omitIgnoredSyncCompareFields, toPrettyJson } from "./deviceSyncCompare";
import useStyles from "./DeviceSync.style";

const downloadJson = (filename: string, pretty: string) => {
  const blob = new Blob([pretty], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const JsonCompare = ({
  entity,
  deviceSnapshot,
  cloudSnapshot
}: {
  entity?: string;
  deviceSnapshot: unknown;
  cloudSnapshot: unknown;
}) => {
  const classes = useStyles();
  const [showFull, setShowFull] = useState(true);
  const same = isEqualJson(deviceSnapshot, cloudSnapshot);
  const deviceJson = omitIgnoredSyncCompareFields(deviceSnapshot);
  const cloudJson = omitIgnoredSyncCompareFields(cloudSnapshot);
  const devicePretty = toPrettyJson(deviceJson);
  const cloudPretty = toPrettyJson(cloudJson);
  const filePrefix = entity || "entity";

  return (
    <Box>
      <Box className={classes.sectionHeader}>
        <Typography variant="subtitle2">
          {same ? t`JSON compare (identical)` : t`JSON compare (differences highlighted)`}
        </Typography>
        <Box className={classes.jsonCompareActions}>
          <Button
            size="small"
            startIcon={<SaveAltIcon fontSize="small" />}
            disabled={!deviceSnapshot}
            onClick={() => downloadJson(`${filePrefix}-device.json`, devicePretty)}
          >
            {t`Save device JSON`}
          </Button>
          <Button
            size="small"
            startIcon={<SaveAltIcon fontSize="small" />}
            disabled={!cloudSnapshot}
            onClick={() => downloadJson(`${filePrefix}-cloud.json`, cloudPretty)}
          >
            {t`Save cloud JSON`}
          </Button>
          <Button size="small" onClick={() => setShowFull((current) => !current)}>
            {showFull ? t`Hide full JSON` : t`Expand full JSON`}
          </Button>
        </Box>
      </Box>
      {showFull && (
        <Box className={classes.diffViewer}>
          <ReactDiffViewer
            oldValue={devicePretty}
            newValue={cloudPretty}
            compareMethod={DiffMethod.LINES}
            splitView={true}
            disableWordDiff={true}
            leftTitle={`📱 ${t`Device`}`}
            rightTitle={`☁️ ${t`Cloud`}`}
            extraLinesSurroundingDiff={3}
          />
        </Box>
      )}
    </Box>
  );
};

export default JsonCompare;
